import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Camera, Upload, Bot, Trash2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fakeAiCaption } from '@/utils/mockData';
import { useToast } from '@/hooks/use-toast';
import { uploadRdoPhoto } from '@/services/rdoMediaStorage';

interface PhotoData {
  id: string;
  file: File;
  caption: string;
  previewUrl: string;
}

/** Foto já enviada ao Storage — shape persistente gravado no payload do RDO. */
export interface SavedPhoto {
  id: string;
  caption: string;
  url: string;
  thumbnailUrl: string;
  previewUrl: string;
  storagePath: string;
}

interface DialogFotosProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (photos: SavedPhoto[]) => void;
  title?: string;
  initialFiles?: FileList | null;
  activityName?: string;
  activityIndex?: number;
}

export const DialogFotos: React.FC<DialogFotosProps> = ({
  open,
  onOpenChange,
  onSave,
  title,
  initialFiles = null,
  activityName,
  activityIndex
}) => {
  const [photos, setPhotos] = useState<PhotoData[]>([]);
  const [processingAi, setProcessingAi] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Generate dynamic title
  const modalTitle = React.useMemo(() => {
    if (title) return title;
    
    if (activityName) {
      return `Adicionar Fotos - ${activityName}`;
    }
    
    if (activityIndex !== null && activityIndex !== undefined) {
      return `Adicionar Fotos - Atividade ${activityIndex + 1}`;
    }
    
    return "Adicionar Fotos";
  }, [title, activityName, activityIndex]);

  // Default caption based on activity name
  const defaultCaption = activityName || '';

  // Processar arquivos iniciais quando o modal abrir
  React.useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0) {
      handleFileSelect(initialFiles);
    }
  }, [open, initialFiles]);

  // Limpar fotos quando o modal fechar
  React.useEffect(() => {
    if (!open) {
      // Clean up object URLs
      photos.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
      setPhotos([]);
    }
  }, [open]);

  const MAX_FILE_SIZE = 3.5 * 1024 * 1024; // 3.5MB

  const validateFile = (file: File): boolean => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erro",
        description: "Apenas arquivos de imagem são permitidos.",
        variant: "destructive"
      });
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "Arquivo muito grande",
        description: `O arquivo ${file.name} excede o limite de 3.5MB.`,
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (validateFile(files[i])) {
        validFiles.push(files[i]);
      }
    }

    const newPhotos: PhotoData[] = validFiles.map(file => ({
      id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      file,
      caption: defaultCaption,
      previewUrl: URL.createObjectURL(file)
    }));

    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const handleCaptionChange = (photoId: string, caption: string) => {
    setPhotos(prev => prev.map(photo => 
      photo.id === photoId ? { ...photo, caption } : photo
    ));
  };

  const handleRemovePhoto = (photoId: string) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo) {
        URL.revokeObjectURL(photo.previewUrl);
      }
      return prev.filter(p => p.id !== photoId);
    });
  };

  const handleAiCaption = async (photoId: string) => {
    setProcessingAi(photoId);
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;

    try {
      const aiCaption = await fakeAiCaption(photo.file);
      handleCaptionChange(photoId, aiCaption);
      toast({
        title: "Legenda gerada",
        description: "Legenda automática gerada com sucesso!"
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao gerar legenda automática.",
        variant: "destructive"
      });
    } finally {
      setProcessingAi(null);
    }
  };

  const handleAiCorrect = async (photoId: string) => {
    setProcessingAi(photoId);
    const photo = photos.find(p => p.id === photoId);
    if (!photo || !photo.caption) return;

    try {
      const correctedCaption = await fakeAiCaption(photo.caption);
      handleCaptionChange(photoId, correctedCaption);
      toast({
        title: "Ortografia corrigida",
        description: "Legenda corrigida com sucesso!"
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao corrigir ortografia.",
        variant: "destructive"
      });
    } finally {
      setProcessingAi(null);
    }
  };

  const handleSave = async () => {
    if (photos.length === 0 || uploading) return;
    setUploading(true);
    try {
      const saved: SavedPhoto[] = await Promise.all(
        photos.map(async (photo) => {
          const { url, path } = await uploadRdoPhoto(photo.file);
          return {
            id: photo.id,
            caption: photo.caption,
            url,
            thumbnailUrl: url,
            previewUrl: url,
            storagePath: path,
          };
        })
      );
      photos.forEach((photo) => {
        if (photo.previewUrl.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
      });
      setPhotos([]);
      onSave(saved);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Falha ao enviar fotos',
        description: 'Não foi possível salvar as imagens. Verifique a conexão e tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    // Clean up object URLs
    photos.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
    setPhotos([]);
    onOpenChange(false);
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {photos.length === 0 ? (
            <div
              className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
              onClick={openFileSelector}
            >
              <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Clique para selecionar fotos ou arraste arquivos aqui
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Máximo 3.5MB por imagem • Formatos: JPG, PNG, GIF, WebP
              </p>
              <Button variant="outline" size="sm">
                <Upload className="w-4 h-4 mr-2" />
                Selecionar Fotos
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {photos.map((photo) => (
                  <div key={photo.id} className="border rounded-lg p-4 space-y-3">
                    <div className="relative">
                      <img
                        src={photo.previewUrl}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-md"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 h-8 w-8 p-0"
                        onClick={() => handleRemovePhoto(photo.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={`caption-${photo.id}`}>Legenda</Label>
                      <div className="flex gap-2">
                        <Input
                          id={`caption-${photo.id}`}
                          value={photo.caption}
                          onChange={(e) => handleCaptionChange(photo.id, e.target.value)}
                          placeholder="Digite a legenda da foto..."
                          className="flex-1"
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={processingAi === photo.id}
                              className="px-3"
                            >
                              <Bot className={cn(
                                "h-4 w-4",
                                processingAi === photo.id && "animate-spin"
                              )} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleAiCaption(photo.id)}>
                              Gerar legenda automática
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleAiCorrect(photo.id)}
                              disabled={!photo.caption}
                            >
                              Corrigir ortografia
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {photo.file.name} • {(photo.file.size / 1024 / 1024).toFixed(2)}MB
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={openFileSelector}
                  className="w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar mais fotos
                </Button>
              </div>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={handleCancel} disabled={uploading}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={photos.length === 0 || uploading}>
            {uploading
              ? 'Enviando...'
              : `Salvar (${photos.length} foto${photos.length !== 1 ? 's' : ''})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};